import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getApiKeys,
  generateApiKey,
  createApiKey,
  updateApiKey,
  regenerateApiKey,
} from '../../services/ApiKeysService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../../Footer';

const emptyForm = {
  name: '',
  key: '',
  email: '',
  phone: '',
  description: '',
  isActive: true,
};

// What the API accepts once it has trimmed and upper-cased the value.
const KEY_PATTERN = /^[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/;

/**
 * Issue and manage API keys. Not linked from the nav — reachable only by typing the URL.
 * The key value itself is generated by the API; keys are never deleted, only deactivated.
 */
function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create/edit slide-over state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState('create'); // 'create' or 'edit'
  const [editingKey, setEditingKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  // Regenerate confirmation state
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  // Rows whose key was just issued or rolled, so the operator can spot it in the table
  const [highlightedIds, setHighlightedIds] = useState([]);

  // Row with a status change in flight (quick activate/deactivate)
  const [togglingId, setTogglingId] = useState(null);

  // Suggested-key state for the create panel
  const [suggesting, setSuggesting] = useState(false);
  const [suggestFailed, setSuggestFailed] = useState(false);
  // Ignores a suggestion that lands after the panel was closed or asked again
  const suggestionRequestRef = useRef(0);

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getApiKeys();
      setApiKeys(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load API keys');
      toast.error(err.message || 'Failed to load API keys');
      setApiKeys([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  /**
   * Pre-fill the key field with a value the API says is free. Nothing is reserved,
   * so it can still be taken before the create lands — that comes back as a 409.
   * If the call fails we leave the field empty, which makes the API generate one
   * on save rather than blocking the create.
   */
  const suggestKey = useCallback(async () => {
    const requestId = suggestionRequestRef.current + 1;
    suggestionRequestRef.current = requestId;
    setSuggesting(true);
    setSuggestFailed(false);
    try {
      const suggestion = await generateApiKey();
      if (requestId !== suggestionRequestRef.current) return;
      setFormData((prev) => ({ ...prev, key: suggestion?.key || '' }));
    } catch (err) {
      if (requestId !== suggestionRequestRef.current) return;
      setSuggestFailed(true);
    }
    if (requestId === suggestionRequestRef.current) setSuggesting(false);
  }, []);

  const openCreatePanel = () => {
    setPanelMode('create');
    setEditingKey(null);
    setFormData(emptyForm);
    setPanelOpen(true);
    suggestKey();
  };

  const openEditPanel = (apiKey) => {
    // Drop any suggestion still in flight from a create panel
    suggestionRequestRef.current += 1;
    setSuggesting(false);
    setSuggestFailed(false);
    setPanelMode('edit');
    setEditingKey(apiKey);
    setFormData({
      name: apiKey.name || '',
      key: apiKey.key || '',
      email: apiKey.email || '',
      phone: apiKey.phone || '',
      description: apiKey.description || '',
      isActive: apiKey.isActive ?? true,
    });
    setPanelOpen(true);
  };

  const closePanel = () => {
    suggestionRequestRef.current += 1;
    setSuggesting(false);
    setSuggestFailed(false);
    setPanelOpen(false);
    setEditingKey(null);
    setFormData(emptyForm);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.warning('Name is required');
      return;
    }

    // The API trims and upper-cases a supplied key before validating it; do the same
    // here so the field can be checked without a round trip.
    const key = formData.key.trim().toUpperCase();
    if (key && !KEY_PATTERN.test(key)) {
      toast.warning('Key must look like XXXXX-XXXXX-XXXXX (letters and digits)');
      return;
    }

    // The API's PUT replaces the whole row, so every field goes out on both paths.
    // A blank key is left out entirely: on create that means "generate one", on edit
    // it means "keep the current one" (sending "" would be a 400).
    const payload = {
      name: formData.name.trim(),
      key,
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      description: formData.description.trim(),
      isActive: formData.isActive,
    };

    setSaving(true);
    try {
      if (panelMode === 'create') {
        const created = await createApiKey(payload);
        toast.success(`API key created for ${created.name}`);
        if (created.id) setHighlightedIds((prev) => [...prev, created.id]);
      } else {
        await updateApiKey(editingKey.id, payload);
        toast.success('API key updated successfully');
      }
      closePanel();
      fetchApiKeys();
    } catch (err) {
      toast.error(err.message || `Failed to ${panelMode === 'create' ? 'create' : 'update'} API key`);
    }
    setSaving(false);
  };

  /** Keys can't be deleted — deactivating is how access is revoked. */
  const handleToggleActive = async (apiKey) => {
    setTogglingId(apiKey.id);
    try {
      await updateApiKey(apiKey.id, {
        name: apiKey.name || '',
        email: apiKey.email || '',
        phone: apiKey.phone || '',
        description: apiKey.description || '',
        isActive: !apiKey.isActive,
      });
      toast.success(apiKey.isActive ? 'API key deactivated' : 'API key activated');
      fetchApiKeys();
    } catch (err) {
      toast.error(err.message || 'Failed to update API key status');
    }
    setTogglingId(null);
  };

  const handleRegenerate = async () => {
    if (!regeneratingKey) return;

    setRegenerating(true);
    try {
      await regenerateApiKey(regeneratingKey.id);
      toast.success('API key regenerated — share the new key with the integration');
      setHighlightedIds((prev) => [...prev, regeneratingKey.id]);
      setRegeneratingKey(null);
      fetchApiKeys();
    } catch (err) {
      toast.error(err.message || 'Failed to regenerate API key');
    }
    setRegenerating(false);
  };

  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success('Key copied to clipboard');
    } catch (err) {
      toast.error('Could not copy the key');
    }
  };

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">API Keys</h4>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-primary d-flex align-items-center"
            onClick={openCreatePanel}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Create API Key
          </button>
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={fetchApiKeys}
            disabled={loading}
            title="Refresh"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise me-2"></i>
            )}
            Refresh
          </button>
        </div>
      </div>

      {loading && apiKeys.length === 0 ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Description</th>
                <th>Status</th>
                <th>Last Used</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No API keys found
                  </td>
                </tr>
              ) : (
                apiKeys.map((apiKey) => (
                  <tr key={apiKey.id} className={highlightedIds.includes(apiKey.id) ? 'table-warning' : undefined}>
                    <td>
                      <span className="fw-semibold">{apiKey.name}</span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <code className="small">{apiKey.key}</code>
                        <button
                          className="btn btn-sm btn-link p-0 text-secondary"
                          onClick={() => handleCopyKey(apiKey.key)}
                          title="Copy key"
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </td>
                    <td className="small text-muted">{apiKey.email || '-'}</td>
                    <td className="small text-muted">{apiKey.phone || '-'}</td>
                    <td className="small text-muted">{apiKey.description || '-'}</td>
                    <td>
                      <span className={`badge ${apiKey.isActive ? 'bg-success' : 'bg-secondary'}`}>
                        {apiKey.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="small text-muted">
                      {apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt).toLocaleString() : 'Never'}
                    </td>
                    <td className="small text-muted">
                      {apiKey.createdAt ? new Date(apiKey.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openEditPanel(apiKey)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => setRegeneratingKey(apiKey)}
                          title="Regenerate key"
                        >
                          <i className="bi bi-arrow-repeat"></i>
                        </button>
                        <button
                          className={`btn btn-sm ${apiKey.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                          onClick={() => handleToggleActive(apiKey)}
                          disabled={togglingId === apiKey.id}
                          title={apiKey.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {togglingId === apiKey.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className={`bi ${apiKey.isActive ? 'bi-slash-circle' : 'bi-check-circle'}`}></i>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/edit slide-over panel */}
      {panelOpen && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closePanel}
            style={{ zIndex: 1040 }}
          ></div>
          <aside
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 400,
              maxWidth: '90vw',
              background: '#fff',
              boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.15)',
              zIndex: 1045,
              display: 'flex',
              flexDirection: 'column',
            }}
            role="dialog"
            aria-label={panelMode === 'create' ? 'Create API key' : 'Edit API key'}
          >
            <div className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
              <span className="fw-bold">
                <i className={`bi ${panelMode === 'create' ? 'bi-plus-lg' : 'bi-pencil'} me-2`}></i>
                {panelMode === 'create' ? 'Create API Key' : 'Edit API Key'}
              </span>
              <button
                type="button"
                className="btn-close"
                onClick={closePanel}
                aria-label="Close"
              ></button>
            </div>

            <form
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}
            >
              <div className="p-3" style={{ overflowY: 'auto', flexGrow: 1 }}>
                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Acme Logistics"
                    disabled={saving}
                    required
                  />
                  <div className="form-text">Who or what this key is issued to</div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted">API Key</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control font-monospace"
                      name="key"
                      value={formData.key}
                      onChange={handleInputChange}
                      placeholder="XXXXX-XXXXX-XXXXX"
                      disabled={saving || suggesting}
                    />
                    {panelMode === 'create' && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={suggestKey}
                        disabled={saving || suggesting}
                        title="Suggest another key"
                      >
                        {suggesting ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-arrow-repeat"></i>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="form-text">
                    {panelMode === 'edit'
                      ? 'Changing this replaces the key immediately — the old one stops working with no recovery. Clear the field to keep the current key.'
                      : suggestFailed
                        ? 'Could not reach the generator. Type a key or leave this blank and one is generated on save.'
                        : 'Suggested by the API. Edit it, or generate another.'}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted">Email</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-envelope"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="integrations@acme.com"
                      disabled={saving}
                    />
                  </div>
                  <div className="form-text">Contact to reach about this key</div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted">Phone</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-telephone"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 312 555 0100"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="What this key is used for"
                    disabled={saving}
                  />
                </div>

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="apiKeyIsActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    disabled={saving}
                  />
                  <label className="form-check-label fw-bold small text-muted" htmlFor="apiKeyIsActive">
                    Active
                  </label>
                  <div className="form-text">Inactive keys are rejected by the API</div>
                </div>

                {panelMode === 'create' && (
                  <div className="alert alert-info mb-0 small">
                    <i className="bi bi-info-circle me-2"></i>
                    The suggested key is not reserved until this is saved. If it gets claimed
                    first the save fails and a new one can be generated.
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end gap-2 px-3 py-3 border-top">
                <button type="button" className="btn btn-secondary" onClick={closePanel} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    panelMode === 'create' ? 'Create' : 'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </aside>
        </>
      )}

      {/* Regenerate Confirmation Modal */}
      {regeneratingKey && (
        <>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setRegeneratingKey(null);
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Regenerate API Key</h5>
                  <button type="button" className="btn-close" onClick={() => setRegeneratingKey(null)}></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to regenerate the key for:</p>
                  <p className="fw-bold mb-1">{regeneratingKey.name}</p>
                  <p className="text-muted small">
                    Current key: <code>{regeneratingKey.key}</code>
                  </p>
                  <div className="alert alert-warning mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    The current key stops working immediately and cannot be recovered.
                    Anything still using it will start failing until it is given the new key.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setRegeneratingKey(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleRegenerate}
                    disabled={regenerating}
                  >
                    {regenerating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Regenerating...
                      </>
                    ) : (
                      'Regenerate'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      <Footer />
    </div>
  );
}

export default ApiKeys;
